import { ThreeRenderer } from './renderer3/threeRenderer';
import { ResourcesContext } from './resources/resourcesContext';
import { SceneGeometryModule } from './scene/sceneGeometryModule';
import type { MultiViewElement } from './multiViewElement';
import * as multiViewElement from './multiViewElement';
import type { SceneViewerConfig } from './sceneViewerConfig';
import * as sceneViewerContext from './sceneViewerContext';

export type MultiViewRendererContext = {
  canvas: HTMLCanvasElement;
  needsUpdate: boolean;
  renderer: ThreeRenderer;
  resources: ResourcesContext;
  readonly views: MultiViewElement[];
};

export const create = () => {
  return { needsUpdate: false, views: [] } as MultiViewRendererContext;
};

export const init = (self: MultiViewRendererContext, canvas: HTMLCanvasElement) => {
  self.canvas = canvas;

  // Init GPU resources
  const renderer = new ThreeRenderer();
  renderer.configureAlpha(true);
  renderer.init(self.canvas);
  renderer.setClearColor('#000000', 0);

  // TODO: Resource context config.
  const resources = new ResourcesContext(renderer);
  self.renderer = renderer;
  self.resources = resources;
  self.needsUpdate = true;
};

export const dispose = (self: MultiViewRendererContext) => {
  // TODO: Dispose pipeline
  // free GPU resources
  self.resources && self.resources.dispose();
  self.resources = undefined;
  self.renderer && self.renderer.dispose();
  self.renderer = undefined;
  // TODO: each view scene reconciler should dispose its own pieces
  const geometryCache = SceneGeometryModule.instance.geometryCache;
  geometryCache.dispose();
  // free DOM references
  self.canvas = undefined;
  self.views.length = 0;
};

export const setSize = (self: MultiViewRendererContext, width: number, height: number) => {
  const devicePixelRatio = window.devicePixelRatio;
  // Update default framebuffer
  self.renderer.setSize(width, height, devicePixelRatio);
  // Mark dirty
  self.needsUpdate = true;
};

export const draw = (self: MultiViewRendererContext) => {
  const renderer = self.renderer.renderer;
  const views = self.views;

  renderer.clear();
  renderer.setScissorTest(true);

  views.forEach((view) => {
    const rect = multiViewElement.getBoundingClientRect(view);

    // check if it's offscreen. If so skip it
    if (
      rect.bottom < 0 ||
      rect.top > renderer.domElement.clientHeight ||
      rect.right < 0 ||
      rect.left > renderer.domElement.clientWidth
    ) {
      return; // it's off screen
    }

    // Configure scissor and viewport
    const width = rect.right - rect.left;
    const height = rect.bottom - rect.top;
    const left = rect.left;
    const bottom = renderer.domElement.clientHeight - rect.bottom;

    renderer.setViewport(left, bottom, width, height);
    renderer.setScissor(left, bottom, width, height);

    // Render view
    sceneViewerContext.render(view);
  });

  renderer.setScissorTest(false);

  for (const view of views) {
    view.needsUpdate = false;
  }
  self.needsUpdate = false;

  onAfterDraw(self);
};

export const createView = (
  self: MultiViewRendererContext,
  config: Partial<SceneViewerConfig>,
  domElement: HTMLElement,
): MultiViewElement => {
  const view = multiViewElement.create(config, self.renderer.renderer, self.resources, domElement);
  self.views.push(view);
  self.needsUpdate = true;
  return view;
};

export const removeView = (self: MultiViewRendererContext, id: string) => {
  const views = self.views;
  const index = views.findIndex((v) => v.id === id);
  views.splice(index, 1);
  self.needsUpdate = true;
};

const onAfterDraw = (self: MultiViewRendererContext) => {
  self.resources.commit();
};
